import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from "react-router-dom";
import { add, format } from 'date-fns';
import { pt } from 'date-fns/locale';
import Cookies from 'universal-cookie';

import { AccreditationMessage, BaseData, Description, EventBanner, EventInformation, TicketsBox } from 'components/event';
import { clearEvent, getEvent } from '../../store/ducks/event/actions';
import {
	hideFixedFooterContent,
	hideMobileOptions,
	showFixedFooterContent,
	showMobileOptions
} from 'store/ducks/nav/actions';
import { dateGlobalPattern } from 'utils/dateUtils';
import { EventState } from 'store/ducks/event/types';
import { Container } from 'styles';
import { Seo } from 'components/standalone';
import PageNotFoundMessage from 'components/pageNotFound/PageNotFoundMessage/PageNotFoundMessage';
import EventService from 'services/EventService';
import FacebookPixelManagement from 'components/layout/facebookPixelManagement/FacebookPixelManagement';
import AdditionalMessage from 'components/event/AdditionalMessage/AdditionalMessage';
import { RenderIf } from 'components/layout';
import { EventMarketingInterface } from 'shared/interfaces/marketing/event-marketing.interface';
import GoogleAdsManagement from 'components/layout/googleAdsManagement/GoogleAdsManagement';
import AnalyticsManagement from 'components/layout/analyticsManagement/AnalyticsManagement';
import { AxiosResponse } from 'axios';
import TiktokPixelManagement from "../../components/layout/tiktokPixelManagment/TiktokPixelManagment";
import { loadRemote } from 'utils/moduleFederation';
import EventDatesCarousel from "../../components/event/EventDatesCarrousel/EventDatesCarrousel";
import {
	EventDatesSubtitle,
	EventDatesTitle,
	EventDatesTitleContainer,
	EventSeatsMapCaption,
	EventSeatsMapContainer,
	EventSeatsMapRef,
	LibLoading,
	MapCaption,
	MapCaptionCircle,
	MapCaptionContainer
} from "./styles";
import EventBottomSideSheet from "../../components/event/BottomSideSheet/EventBottomSideSheet";
import { useEvent } from "./useEvent";
import { useSeatsMapAddToCart } from "./useSeatsMapAddToCart";
import LoadingSpinner from "../../components/event/LoadingSpinner/LoadingSpinner";

interface StateProps {
	event: EventState,
}

const EventPage = () => {
	const { url } = useParams<{ url: string }>();
	const select = (state: StateProps) => state.event;
	const dispatch = useDispatch();
	const history = useHistory();
	const [affiliateCode, setAffiliateCode] = useState<string | null>(null);
	const [ticketToken, setTicketToken] = useState<string | null>(null);
	const [dataMarketing, setDataMarketing] = useState<EventMarketingInterface[] | null>(null);
	const { data, error } = useSelector(select);
	const { handleCheckout } = useEvent(Number(data.id));
	const handleAddTicket = useSeatsMapAddToCart();
	const { search } = useLocation();

	const seatsMapRef = React.useRef<HTMLDivElement>(null);
	const seatsMapRefContainer = React.useRef<HTMLDivElement>(null);

	const [meepId, setMeepId] = useState<string>('');
	const [libLoaded, setLibLoaded] = useState(false);
	const [stageWidth, setStageWidth] = useState<number | null>(null);
	const [eventDateId, setEventDateId] = useState<number | null>(null);
	const [openSeatsMap, setOpenSeatsMap] = useState<boolean>(false);
	const [purchaseMode, setPurchaseMode] = useState<'map' | 'outside'>('map');

	const showTickets = useMemo(() => data.status === 'PUBLISHED' && !data.isAccreditation,
		[data.status, data.isAccreditation])
	const orderedEventDates = useMemo(() => {
		if (!data.eventDates) return [];
		return data.eventDates.sort(
			(a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
		);
	}, [data.eventDates]);

	useEffect(() => {
		dispatch(clearEvent());
		dispatch(hideMobileOptions());
		dispatch(showFixedFooterContent());
		getMeepId();
	}, []);

	useEffect(() => {
		dispatch(getEvent(url));
	}, [dispatch]);

	useEffect(() => {
		if (data.url == url) {
			window.scrollTo({top: 0, behavior: "smooth"});
			getTicketToken();
			getAffiliateCode(data.id);
			callCountEventView(data.id);
			getEventMarketingData();
		}
	}, [data.url]);

	useEffect(() => {
		if (isMobile) return;
		if (openSeatsMap && data.eventDates?.length) {
			setEventDateId(data.eventDates[0].id);
			if (data.seatsMapActived && !customElements.get('seats-map-element')) {
				const remoteUrl = process.env.REACT_APP_SEATS_MAP_MF_URL || 'http://localhost:5001/assets/remoteEntry.js';
				console.log('EventPage: Loading remote from:', remoteUrl);
				loadRemote(remoteUrl)
					.then(() => {
						console.log('EventPage: Remote loaded successfully');
						setLibLoaded(true);
					})
					.catch(err => console.error("EventPage: Failed to load Seats Map Microfrontend:", err));
			} else if (customElements.get('seats-map-element')) {
				setLibLoaded(true);
			}
		}
	}, [data.eventDates, openSeatsMap]);

	useEffect(() => {
		if (!libLoaded) return;

		const seatsMapElement =
			seatsMapRef.current?.querySelector('seats-map-element');

		if (!seatsMapElement) return;

		const checkoutEventListener = () => {
			handleCheckout();
		};

		const purchaseModeChangeListener = ((event: CustomEvent<{ mode: 'map' | 'outside' }>) => {
			setPurchaseMode(event.detail.mode);
		}) as EventListener;

		seatsMapElement.addEventListener('checkout-event', checkoutEventListener);
		seatsMapElement.addEventListener('add-ticket', handleAddTicket);
		seatsMapElement.addEventListener('purchase-mode-change', purchaseModeChangeListener);

		return () => {
			seatsMapElement.removeEventListener('checkout-event', checkoutEventListener);
			seatsMapElement.removeEventListener('add-ticket', handleAddTicket);
			seatsMapElement.removeEventListener('purchase-mode-change', purchaseModeChangeListener);
		};
	}, [libLoaded, openSeatsMap, stageWidth, handleCheckout, handleAddTicket]);

	useEffect(() => {
		if (!openSeatsMap) {
			setPurchaseMode('map');
		}
	}, [openSeatsMap]);

	useEffect(() => {
		if (!openSeatsMap || !seatsMapRefContainer.current) {
			setStageWidth(null);
			return;
		}

		requestAnimationFrame(() => {
			const width = getContentWidth(seatsMapRefContainer.current!);
			setStageWidth(width);
		});
	}, [openSeatsMap]);


	useEffect(() => {
		return () => {
			setLibLoaded(false);
			dispatch(showMobileOptions());
			dispatch(hideFixedFooterContent());
			dispatch(clearEvent());
		}
	}, []);


	const getContentWidth = (el: HTMLElement) => {
		const styles = window.getComputedStyle(el);

		const paddingLeft = parseFloat(styles.paddingLeft);
		const paddingRight = parseFloat(styles.paddingRight) + 12;

		return el.clientWidth - paddingLeft - paddingRight;
	};


	const getEventMarketingData = async () => {
		if (!data) return

		try {
			const res: AxiosResponse<EventMarketingInterface[]> = await EventService.getMarketingData(data?.id);
			setDataMarketing(Array.isArray(res.data) ? res.data : null);
		} catch (error) {
			console.error('Error fetching event marketing data:', error);
			setDataMarketing(null);
		}
	}

	const getQueryParams = (param: string) => {
		let query = new URLSearchParams(search);
		return query.get(param) || null;
	}

	const dateHandler = (startDate: string): string => {
		return startDate ? format(new Date(dateGlobalPattern(startDate)), "dd 'de' MMMM 'de' yyyy',' HH:mm", { locale: pt }) : '';
	}

	const getAffiliateCode = (eventId: string) => {
		const affiliateCode = getQueryParams('a');
		const cookies = new Cookies();
		const affiliateCookie = cookies.get(url);

		if (affiliateCode) {
			if (!affiliateCookie) {
				EventService.checkAffiliate(eventId, affiliateCode)
					.then(() => cookies.set(url, affiliateCode, { expires: add(new Date(), { 'weeks': 1 }) }));
			}
			setAffiliateCode(affiliateCode);
			return;
		}
		setAffiliateCode(affiliateCookie);
	}

	const getTicketToken = () => {
		const token = getQueryParams('vt');
		setTicketToken(token);
	}

	const getMeepId = () => {
		const mid = getQueryParams('mid') || '';
		setMeepId(mid);
	}

	const callCountEventView = async (eventId: string) => {
		const params = { mid: meepId }
		EventService.countViews(eventId, params)
			.then(({ data }) => setTrackingView(eventId, data));
	}

	const setTrackingView = (eventId: string, hashKey: string) => {
		const payload = {
			latitude: '', longitude: '',
		};
		const params = { hashKey };

		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(pos => {
				payload.latitude = pos.coords.latitude.toString();
				payload.longitude = pos.coords.longitude.toString();
				callTrackingView(eventId, params, payload);
			})
		}
	}

	const callTrackingView = (eventId: string, params: any, payload: any) => {
		EventService.countTrackingViews(eventId, params, payload);
	}

	const checkCartByEvent = () => {
		const currentEventId = sessionStorage.getItem('currentEventId');
		if (currentEventId && currentEventId !== data.id.toString()) {
			sessionStorage.removeItem('cartId');
			sessionStorage.removeItem('cartStartTime');
		}
		sessionStorage.setItem('currentEventId', data.id);
	}

	const handleBuySeatsMapTickets = () => {
		checkCartByEvent();
		if (!isMobile) {
			setOpenSeatsMap(true);
			return;
		}
		setOpenSeatsMap(false);
		const previousPathName = history.location.pathname;
		history.push({ pathname: `${previousPathName}/sections` });
	}

	return (<>
		{error ? <PageNotFoundMessage /> : (<>
			{dataMarketing && <>
				<FacebookPixelManagement data={dataMarketing} eventId={data.id} />
				<GoogleAdsManagement data={dataMarketing} eventId={data.id} />
				<AnalyticsManagement data={dataMarketing} eventId={data.id} />
				<TiktokPixelManagement data={dataMarketing} eventId={data.id} />
			</>}
			<Seo
				title={`${process.env.REACT_APP_NAME} - ${data.name}`}
				description={`${data.name} - ${dateHandler(data.startDate)}`}
				ogDescription={dateHandler(data.startDate)}
				image={data.thumbnail}
				url={data.url}
			/>
			<EventBanner image={data?.featuredBanner ? data?.featuredBanner : data?.banner} />

			<Container padding={isMobile ? 10 : 40}>
				<EventInformation>
					<EventDescriptionWrapper>
						<BaseData
							name={data.name}
							startDate={data.startDate}
							endDate={data.endDate}
							fullAddress={data.fullAddress}
							locationType={data.locationType}
							status={data.status}
							customDate={data.customDate}
						/>

						<Description
							eventId={data.id}
							status={data.status}
							tags={data.tags ? data.tags.split(',') : []}
						/>

						<RenderIf condition={data.showCancellationDescription}>
							<AdditionalMessage eventId={data.id} />
						</RenderIf>

						<RenderIf condition={data.isAccreditation}>
							<AccreditationMessage eventName={data.name} eventUrl={data.url} />
						</RenderIf>
					</EventDescriptionWrapper>

					<RenderIf condition={showTickets}>
						<div>
							<TicketsBox
								eventId={data.id}
								meepId={meepId}
								limitedByCpf={data.limitedByCpf}
								affiliateCode={affiliateCode}
								ticketToken={ticketToken}
								salesStartTime={data.salesStartDateInSeconds}
								hasMarketing={data.hasMarketing}
								grouped={data.groupBySector}
								hasDiscount={data.hasDiscount}
								showTickets={!data.seatsMapActived}
								handleBuyTickets={handleBuySeatsMapTickets}
							/>
						</div>
					</RenderIf>
				</EventInformation>

				<EventBottomSideSheet
					isOpen={openSeatsMap && !isMobile}
					header={
						<EventDatesTitleContainer>
							<EventDatesTitle>Selecione uma data</EventDatesTitle>
							<EventDatesSubtitle>
								Horários referentes ao local do evento.
							</EventDatesSubtitle>
						</EventDatesTitleContainer>
					}
					onClose={() => setOpenSeatsMap(false)}
				>
					<EventSeatsMapContainer ref={seatsMapRefContainer} >
						{libLoaded ? (
							<>
								{orderedEventDates && (
									<>
										<EventDatesCarousel
											dates={orderedEventDates}
											selectedId={eventDateId}
											onChange={setEventDateId}
										/>

										<EventSeatsMapRef ref={seatsMapRef}>
											{stageWidth && (
												<seats-map-element
													tenant-id={process.env.REACT_APP_TENANT_ID}
													event-id={data.id}
													event-dates={JSON.stringify(data.eventDates)}
													event-date-id={eventDateId}
													view-mode="VIEWER"
													stage-width={stageWidth}
													has-outside-map-sectors={data.hasOutsideMapSectors ? 'true' : 'false'}
													initial-mode="map"
												/>
											)}

											{purchaseMode === 'map' && (
												<EventSeatsMapCaption>
													<MapCaptionContainer>
														<MapCaption>
															<MapCaptionCircle $color={'#32a6f8'} />
															Selecionado
														</MapCaption>

														<MapCaption>
															<MapCaptionCircle $color={'#ececec'} />
															Indisponível
														</MapCaption>
													</MapCaptionContainer>
												</EventSeatsMapCaption>
											)}
										</EventSeatsMapRef>
									</>
								)}
							</>
						) : (<LoadingSpinner />)}
					</EventSeatsMapContainer>
				</EventBottomSideSheet>
			</Container>
		</>)}
	</>);
};

export default EventPage;

export const EventDescriptionWrapper = styled.div`
	@media (min-width: 991px) {
		max-width: calc(100% - 380px);
		width: 100%;
	}
`;
